import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Divider,
  Alert
} from '@mui/material';
import Layout from '../components/layout/Layout';

interface LogEntry {
  timestamp: string;
  message: string;
  type: 'info' | 'success' | 'error' | 'warning';
}

const DebugTestPage: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [deviceInfo, setDeviceInfo] = useState({
    userAgent: '',
    platform: '',
    isMobile: false
  });
  const logContainerRef = useRef<HTMLDivElement>(null);
  const invitationId = 'test123';

  useEffect(() => {
    // Initialize device info
    setDeviceInfo({
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    });

    // Listen for visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        log('👁️ Page became hidden (app might have opened)', 'success');
      } else {
        log('👁️ Page became visible again', 'info');
      }
    };

    // Listen for blur/focus events
    const handleBlur = () => {
      log('🔍 Window lost focus', 'info');
    };

    const handleFocus = () => {
      log('🔍 Window gained focus', 'info');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    log('📱 Debug page loaded successfully', 'success');

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new logs are added
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const log = (message: string, type: LogEntry['type'] = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const newLog: LogEntry = { timestamp, message, type };
    setLogs(prev => [...prev, newLog]);
    console.log(`[${timestamp}] ${message}`);
  };

  const clearLog = () => {
    setLogs([]);
  };

  const testCustomScheme = () => {
    const url = `spendifly://invite/${invitationId}`;
    log(`Testing custom scheme: ${url}`, 'info');

    try {
      window.location.href = url;
      log('✅ Custom scheme triggered successfully', 'success');

      setTimeout(() => {
        if (!document.hidden) {
          log('❌ App did not open (page still visible)', 'error');
        } else {
          log('✅ App appears to have opened (page hidden)', 'success');
        }
      }, 2000);
    } catch (e) {
      log(`❌ Custom scheme error: ${(e as Error).message}`, 'error');
    }
  };

  const testUniversalLink = () => {
    const url = `https://gamiflytech.com/spendifly/invite/${invitationId}`;
    log(`Testing universal link: ${url}`, 'info');

    try {
      window.location.href = url;
      log('✅ Universal link triggered successfully', 'success');

      setTimeout(() => {
        if (!document.hidden) {
          log('❌ Universal link fallback (stayed on web)', 'warning');
        } else {
          log('✅ App appears to have opened via universal link', 'success');
        }
      }, 2000);
    } catch (e) {
      log(`❌ Universal link error: ${(e as Error).message}`, 'error');
    }
  };

  const testWindowLocation = () => {
    const url = `spendifly://invite/${invitationId}`;
    log(`Testing window.location: ${url}`, 'info');

    try {
      const startTime = Date.now();
      window.location.href = url;

      setTimeout(() => {
        const elapsed = Date.now() - startTime;
        if (elapsed > 1000) {
          log(`✅ Possible app launch (${elapsed}ms delay)`, 'success');
        } else {
          log(`❌ Quick return suggests app not found (${elapsed}ms)`, 'error');
        }
      }, 1500);
    } catch (e) {
      log(`❌ Window.location error: ${(e as Error).message}`, 'error');
    }
  };

  const testIframe = () => {
    const url = `spendifly://invite/${invitationId}`;
    log(`Testing iframe method: ${url}`, 'info');

    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      log('✅ Iframe created successfully', 'success');

      setTimeout(() => {
        document.body.removeChild(iframe);
        log('🗑️ Iframe removed', 'info');

        if (!document.hidden) {
          log('❌ App did not open via iframe', 'error');
        } else {
          log('✅ App appears to have opened via iframe', 'success');
        }
      }, 2000);
    } catch (e) {
      log(`❌ Iframe error: ${(e as Error).message}`, 'error');
    }
  };

  const testAllMethods = () => {
    log('🚀 Testing all methods in sequence...', 'info');

    setTimeout(() => testCustomScheme(), 500);
    setTimeout(() => testIframe(), 3000);
    setTimeout(() => testWindowLocation(), 5500);
    setTimeout(() => testUniversalLink(), 8000);
  };

  const getLogColor = (type: LogEntry['type']) => {
    switch (type) {
      case 'success': return 'green';
      case 'error': return 'red';
      case 'warning': return 'orange';
      default: return 'inherit';
    }
  };

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
                        <Typography variant="h3" component="h1" gutterBottom>
                🔗 Spendifly Deep Link Debug Test
              </Typography>
          
          <Typography variant="h6" gutterBottom>
            <strong>Invitation ID:</strong> {invitationId}
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Device Info:</strong>
            </Typography>
            <Typography variant="body2">
              <strong>User Agent:</strong> {deviceInfo.userAgent}
            </Typography>
            <Typography variant="body2">
              <strong>Platform:</strong> {deviceInfo.platform}
            </Typography>
            <Typography variant="body2">
              <strong>Mobile:</strong> {deviceInfo.isMobile ? 'Yes' : 'No'}
            </Typography>
          </Alert>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Test Methods:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={testCustomScheme}
              sx={{ py: 1.5 }}
            >
              🎯 Test Custom Scheme (spendifly://)
            </Button>

            <Button
              variant="contained"
              fullWidth
              onClick={testUniversalLink}
              sx={{ py: 1.5 }}
            >
              🌐 Test Universal Link (https://)
            </Button>

            <Button
              variant="contained"
              fullWidth
              onClick={testWindowLocation}
              sx={{ py: 1.5 }}
            >
              📱 Test Window Location
            </Button>

            <Button
              variant="contained"
              fullWidth
              onClick={testIframe}
              sx={{ py: 1.5 }}
            >
              🖼️ Test Iframe Method
            </Button>

            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={testAllMethods}
              sx={{ py: 1.5 }}
            >
              🚀 Test All Methods
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" gutterBottom>
            Debug Log:
          </Typography>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box
                ref={logContainerRef}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: '#f8f8f8',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 1.5,
                  minHeight: '100px'
                }}
              >
                {logs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No logs yet...
                  </Typography>
                ) : (
                  logs.map((log, index) => (
                    <Box
                      key={index}
                      sx={{
                        color: getLogColor(log.type),
                        marginBottom: '4px'
                      }}
                    >
                      [{log.timestamp}] {log.message}
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>

          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={clearLog}
            sx={{ py: 1.5 }}
          >
            🗑️ Clear Log
          </Button>
        </Paper>
      </Container>
    </Layout>
  );
};

export default DebugTestPage; 